import { Grid, Typography } from "@mui/material";
import { Box, Container, styled } from "@mui/system";
import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useTenantConfig } from "../hooks/useTenant";


/* Section badge (pill) - Kurumsal Blue */
const SectionBadge = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    borderRadius: 50,
    background: 'rgba(30, 64, 175, 0.06)',
    border: '1px solid rgba(30, 64, 175, 0.12)',
    marginBottom: 20,
});



const fadeUp = {
    hidden: { opacity: 0, y: 36 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.65,
            ease: [0.22, 1, 0.36, 1],
            delay: i * 0.1,
        },
    }),
};

const OfficialInfrastructureSectionKurumsal = () => {


    const {colors,tenantName} = useTenantConfig();
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-80px' });

    const FEATURES = [
        {
            image: '/assets/icon/contract.webp',
            title: 'E-İmza Destekli Belge Yönetimi',
            desc: 'Resmi belgelerinizi elektronik imza ile güvenli ve doğrulanabilir şekilde oluşturun.',
        },
        {
            image: '/assets/icon/security.webp',
            title: 'Mevzuata Uygun EYP Üretimi',
            desc: 'Resmi yazışma mevzuatına uygun Elektronik Yazışma Paketi (EYP) üretin.',
        },
        {
            image: '/assets/icon/email.webp',
            title: 'Entegre KEP Sistemi',
            desc: 'Kayıtlı Elektronik Posta (KEP) entegrasyonu ile kamu kurumlarıyla doğrudan iletişim kurun.',
        },
        {
            image: '/assets/icon/folder.webp',
            title: 'Güvenli ve Denetlenebilir Arşiv',
            desc: 'Tüm resmi belgeler kayıt altına alınır, arşivlenir ve denetime hazır tutulur.',
        },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: { xs: 8, md: 14 },
                background: 'linear-gradient(165deg, rgba(239, 246, 255, 0.5) 0%, #f8fafc 50%, #ffffff 100%)',
                borderBottom: '1px solid #e5e7eb',
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">

                    {/* SOL */}
                    <Grid item xs={12} md={5}>
                        <motion.div
                        >
                            <SectionBadge>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                                    }}
                                />
                                <Typography sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    letterSpacing: '0.5px',
                                }}>
                                    SADECE WEB SİTESİ DEĞİL
                                </Typography>
                            </SectionBadge>

                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: { xs: '2.4rem', md: '3rem' },
                                    color: '#0f172a',
                                    lineHeight: 1.2,
                                    letterSpacing: '-0.02em',
                                    mb: 2.5,
                                }}
                            >
                                Resmi Altyapılı {' '}
                                <Box
                                    component="span"
                                    sx={{
                                        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        fontWeight: 800,
                                    }}
                                >
                                    Dijital Dernek Yönetimi
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '1rem',
                                    lineHeight: 1.75,
                                    mb: 4,
                                }}
                            >
                                {tenantName}, dernek web sitesi oluşturmanın ötesinde,
                                kamu kurumlarıyla uyumlu resmi yazışma süreçlerini destekleyen
                                e-imza tabanlı bir dijital yönetim platformudur.
                            </Typography>

                            <Box
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.04) 0%, rgba(8, 145, 178, 0.04) 100%)',
                                    border: '1px solid rgba(30, 64, 175, 0.12)',
                                }}
                            >
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
                                    Turkcell bulut altyapısı üzerinde çalışan,
                                    SLA destekli ve denetime hazır platform.
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* SAĞ */}
                    <Grid item xs={12} md={7}>
                        <motion.div
                        >
                            <Grid container spacing={2}>
                                {FEATURES.map((f, i) => {
                                    return (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <motion.div
                                                variants={fadeUp}
                                                custom={i + 1}
                                                initial="hidden"
                                                animate={isInView ? 'visible' : 'hidden'}
                                                style={{ height: '100%' }}
                                            >
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        borderRadius: 3,
                                                        border: '1px solid rgba(30, 64, 175, 0.08)',
                                                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(239, 246, 255, 0.4) 100%)',
                                                        height: '100%',
                                                        transition: 'all 0.25s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: '0 12px 32px rgba(30, 64, 175, 0.12)',
                                                            borderColor: 'rgba(30, 64, 175, 0.15)',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 70,
                                                            height: 70,
                                                            borderRadius: 2.5,
                                                            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
                                                            boxShadow: '0 8px 20px rgba(30, 64, 175, 0.25)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mb: 2.5,
                                                        }}
                                                    >
                                                        <img
                                                            src={f.image}
                                                            alt={f.title}
                                                            style={{
                                                                width: '65%',
                                                                height: '65%',
                                                                filter: 'brightness(0) invert(1)'
                                                            }}
                                                        />
                                                    </Box>

                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: '0.95rem',
                                                            color: '#0f172a',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        {f.title}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.85rem',
                                                            color: '#64748b',
                                                            lineHeight: 1.6,
                                                        }}
                                                    >
                                                        {f.desc}
                                                    </Typography>
                                                </Box>
                                            </motion.div>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </motion.div>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
};

export default OfficialInfrastructureSectionKurumsal;